// SAD-125: presentation layer for the operational-audit timeline's field
// diffs. The stored payloads are raw snake_case state snapshots
// (`assignment_type: (empty) -> primary`, `role_bundle_key: (empty) ->
// quality_audit`, `effective_end_present: -> false`), which read like a
// database dump. This turns one diff into a row a program viewer can read -
// friendly label, humanized value, empty/false/id noise dropped - WITHOUT
// touching the raw payload, which stays available behind the row expand.
//
// It is driven by a small label/value map plus a few rules, never per-action
// hardcoding, so a new action's payload gets a reasonable rendering for free.
//
// Masking note: a masked/simplified viewer's diffs arrive already blanked from
// the server (payloads_withheld), so this only ever sees values the viewer is
// entitled to - it can never resurrect a withheld value.
// Friendly labels for the snake_case field keys the audit state payloads carry.
// A key absent here falls back to a generic snake_case -> Title Case humanizer,
// so an unmapped field still reads as English rather than as a column name.
const FIELD_LABELS = {
    assignment_type: 'Type',
    role_bundle_key: 'Role Bundle',
    type: 'Type',
    category: 'Category',
    state: 'State',
    status: 'Status',
    comment: 'Comment',
    reason: 'Reason',
    field_path: 'Field',
    // Identity/reference fields (SAD-125 points 1/3/5): these carry a payload
    // value that reference_display_names may resolve to a name. original_owner
    // has no "_id" suffix - a key-name heuristic would miss it - which is why
    // resolution below is value-driven, not key-driven; the label map only
    // decides what the row is CALLED once a value is kept.
    original_owner: 'Original Owner',
    owner_user_id: 'Owner',
    delegate_user_id: 'Delegate',
    requester_user_id: 'Requester',
    target: 'Target'
};
// humanizeFieldLabel maps a snake_case field key to its display label, falling
// back to Title Case for anything unmapped.
export function humanizeFieldLabel(field) {
    const mapped = FIELD_LABELS[field];
    if (mapped) return mapped;
    return titleCaseTokens(field);
}
// Same identifier shapes the server's value-driven resolver matches
// (lineage_audit.go's userIdentifierPattern / objectReferencePattern) - a bare
// pangaea user id, or an "objectType/objectId" path. Used here only to STOP
// the enum-token title-caser from mangling an unresolved id into a garbled
// near-original string (e.g. "a27e19cc..." -> "A27e19cca0c11b28455a6ab3a...");
// actual resolution to a name happens via reference_display_names, not here.
const USER_ID_SHAPE = /^[0-9a-f]{32}$/;
const OBJECT_REFERENCE_SHAPE = /^[a-zA-Z][a-zA-Z0-9_.]*\/[0-9a-f]{32}$/;
function isIdentifierShaped(value) {
    return USER_ID_SHAPE.test(value) || OBJECT_REFERENCE_SHAPE.test(value);
}
// humanizeFieldValue renders one payload value for a human: an enum-ish token
// (all-lower or all-upper, snake or flat) becomes Title Case (primary ->
// "Primary", quality_audit -> "Quality Audit", WAIVER -> "Waiver"); a value
// that is already a human phrase (has a space, or is a mixed-case word like
// "Requested") is left untouched. An unresolved identifier-shaped value is
// left raw rather than title-cased - title-casing hex is mangling, not
// humanizing.
export function humanizeFieldValue(value) {
    const v = value.trim();
    if (!v) return '';
    if (isIdentifierShaped(v)) return v;
    if (v.includes(' ')) return v; // already a human phrase
    const isEnumToken = /^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*$/.test(v) && (v === v.toLowerCase() || v === v.toUpperCase());
    if (!isEnumToken) return v; // mixed-case single word (e.g. "Requested") - already human
    return titleCaseTokens(v);
}
function titleCaseTokens(s) {
    return s.split('_').filter(Boolean).map((w)=>w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}
// A pure identifier is bookkeeping the viewer does not read (scope_node_id,
// role_grant_event_id, …) - it is dropped from the humanized summary and stays
// in the raw expand.
function isBookkeepingField(field) {
    return field.endsWith('_id');
}
// Empty, "false", or absent - the values matt asked us to drop entirely ("if no
// end date, no need to even put it, much less showing false"). The timeline
// shows what a workflow SET; a field left empty or turned off is not a row.
// SAD-788: "false" is emptyish only as an AFTER value (the field was turned
// off). As a BEFORE value of a real change ("false" → "true"), it must stay so
// the row reads "Exercised: False -> True".
function isEmptyish(value) {
    const v = value.trim().toLowerCase();
    return v === '' || v === 'false';
}
function isAbsent(value) {
    return value.trim() === '';
}
// A raw payload token resolves to a name only via an exact match in
// reference_display_names (server-populated, value-driven - see
// opsAuditFormat.ts's header). Never guess a resolution client-side: a miss
// must fall through to today's raw-value handling, not a fabricated name.
function resolveReference(value, referenceDisplayNames) {
    const v = value.trim();
    if (!v) return undefined;
    const name = referenceDisplayNames[v];
    return name?.trim() ? name.trim() : undefined;
}
function tryParseJsonObject(value) {
    const v = value.trim();
    if (!v.startsWith('{') || !v.endsWith('}')) return undefined;
    try {
        const parsed = JSON.parse(v);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : undefined;
    } catch  {
        return undefined;
    }
}
// change_set.item.applied's object-level-add case (SAD-125 point 6): an
// object-level add has no single field_path, so the server emits one diff row
// keyed by the added object's type (e.g. "Contact") whose After is the whole
// added object as JSON. A bare object-type label over raw JSON still reads
// like a database dump, so this describes what was actually added from the
// object's own content - value-driven, the same way the rest of this module
// works, rather than hardcoding a `change_set.item.applied` special case.
// Returns undefined (falls back to the raw-JSON rendering) when the value
// isn't a JSON object, there was a real prior value (a field EDIT, not an
// add), or the object carries none of the name-ish fields this recognizes.
function describeObjectLevelAdd(before, after) {
    if (!isEmptyish(before)) return undefined;
    const obj = tryParseJsonObject(after);
    if (!obj) return undefined;
    const firstName = typeof obj.first_name === 'string' ? obj.first_name : '';
    const lastName = typeof obj.last_name === 'string' ? obj.last_name : '';
    const fullName = [
        firstName,
        lastName
    ].filter(Boolean).join(' ');
    const name = fullName || (typeof obj.name === 'string' ? obj.name : '');
    const email = typeof obj.email === 'string' ? obj.email : '';
    if (name && email) return `${name} (${email})`;
    if (name) return name;
    if (email) return email;
    return undefined;
}
// humanizeDiffs turns the raw field diffs into the rows the summary renders:
// bookkeeping ids dropped, empty/false values dropped, empty->value collapsed,
// labels and values humanized, and identity/reference values resolved to a
// name where the server was able to. Order is preserved (the server already
// sorts). `referenceDisplayNames` is the event's map, exact-raw-token keyed;
// omit it (or pass an empty map, as a masked view does) and every value
// degrades to today's raw/dropped behavior - it never crashes and never
// claims a name it doesn't have.
export function humanizeDiffs(diffs, referenceDisplayNames = {}) {
    const out = [];
    for (const d of diffs){
        // Resolution is checked BEFORE the bookkeeping-id drop and BEFORE the
        // enum humanizer: a resolved identity is never dropped just because its
        // key ends in "_id" (delegation.approved's owner_user_id/delegate_user_id)
        // and is never mangled just because it has no "_id" suffix
        // (role.revoked's original_owner).
        const resolvedAfter = resolveReference(d.after, referenceDisplayNames);
        const resolvedBefore = resolveReference(d.before, referenceDisplayNames);
        if (resolvedAfter !== undefined || resolvedBefore !== undefined) {
            const after = resolvedAfter ?? (isEmptyish(d.after) ? '' : humanizeFieldValue(d.after));
            if (after === '') continue;
            const before = resolvedBefore ?? (isAbsent(d.before) ? '' : humanizeFieldValue(d.before));
            out.push({
                label: humanizeFieldLabel(d.field),
                before,
                after,
                collapsed: before === ''
            });
            continue;
        }
        if (isBookkeepingField(d.field)) continue;
        const added = describeObjectLevelAdd(d.before, d.after);
        if (added) {
            out.push({
                label: `${humanizeFieldLabel(d.field)} added`,
                before: '',
                after: added,
                collapsed: true
            });
            continue;
        }
        // The AFTER value is what the workflow left in place; if it is empty/false,
        // the field carries nothing worth a row (drops "End: false", empty sets).
        // BEFORE keeps a real prior "false" so boolean flips read old → new (SAD-788).
        const after = isEmptyish(d.after) ? '' : humanizeFieldValue(d.after);
        if (after === '') continue;
        const before = isAbsent(d.before) ? '' : humanizeFieldValue(d.before);
        out.push({
            label: humanizeFieldLabel(d.field),
            before,
            after,
            collapsed: before === ''
        });
    }
    return out;
}
